'use client'

import { AxiosError } from 'axios'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { pushToDataLayer } from 'lib/analytics/dataLayer'
import { identifyUser, posthogClient } from 'lib/analytics/posthog'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { User } from 'types/auth'

import { getApiClient } from '@/api'
import { showNotification } from '@/components/Notification'
import { API_ROUTES, ROUTES, SEARCH_PARAMS, UserRoleEnum } from '@/constants'
import { qrCodesApi } from '@/generator/shared'
import { userAtom } from '@/global-state'
import { useRouter } from '@/i18n-routing'
import { THideSocials } from '@/utils'
import { getUserTimezone } from '@/utils/timezoneUtils'
import { getOnboardingLandingRoute } from '@/utils/user/getOnboardingLandingRoute'
import { setLastAuthProvider } from '@/utils/user/lastAuthProvider'

import { getValidErrorMessage } from '../../utils/apiUtils'
import {
	getBuilderSessionId,
	getOnboardingQrId,
	isPostSignUpVisitAtom,
	setOnboardingQrId,
	signUpDefaultEmailAtom,
} from '../generator/generatorState'
import { pending2faAccessTokenAtom, setAuthToken } from './atoms'
import { SignUpExistingAccountStep } from './SignUpExistingAccountStep'
import { SignUpPageDirect } from './SignUpPageDirect'
import { SignUpPageWelcome } from './SignUpPageWelcome'
import { setNewUserCookie } from './utils/authCookies'
import { DEFAULT_HIDE_SOCIALS } from './utils/hideSocials'
import { isNewlyCreatedAccount } from './utils/isNewAccount'
import {
	DEFAULT_OTP_RESEND_COOLDOWN_SECONDS,
	isLoginChallenge,
	TAuthSuccess,
	TLoginChallenge,
} from './utils/loginChallenge'
import {
	getNextSignUpDefaultEmail,
	getPayloadPreviousEmail,
} from './utils/signUpDefaultEmail'

const isExistingAccountConflict = (error: unknown): boolean =>
	error instanceof AxiosError &&
	error.response?.status === 409 &&
	error.response.data?.message ===
		'api.errors.signup.providersUserAlreadyExists'

export function SignUpContainer({
	isOnboardingFlow,
	isPopupMode = false,
	locale,
	user,
	hideSocials = DEFAULT_HIDE_SOCIALS,
	hidePassword = false,
	defaultEmail,
	previousEmail,
	onboardingQrId,
}: {
	isOnboardingFlow: boolean
	isPopupMode?: boolean
	locale: string
	user: User | null
	hideSocials?: THideSocials
	hidePassword?: boolean
	defaultEmail?: string
	previousEmail?: string
	onboardingQrId?: string | null
}) {
	const timezone = getUserTimezone()
	const t = useTranslations()

	const [atomUser, setUser] = useAtom(userAtom)
	const setIsPostSignUpVisit = useSetAtom(isPostSignUpVisitAtom)
	const setPending2faAccessToken = useSetAtom(pending2faAccessTokenAtom)
	const setSignUpDefaultEmail = useSetAtom(signUpDefaultEmailAtom)
	const storedDefaultEmail = useAtomValue(signUpDefaultEmailAtom)
	const [isLoading, setIsLoading] = useState(false)
	const [otpStep, setOtpStep] = useState<{
		email: string
		resendCooldownSeconds: number
	} | null>(null)
	const [passwordEmail, setPasswordEmail] = useState<string | null>(null)
	const containerId = isPopupMode ? 'dialog' : 'notification'

	const router = useRouter()
	const [isRedirectChecked, setIsRedirectChecked] = useState(false)
	// This might seem redundant because of the server side check, but when back button is clicked
	// from the generator, the server side check is not triggered, so we need to check it here
	useEffect(() => {
		if (isPopupMode) {
			setIsRedirectChecked(true)
			return
		}

		const currentUser = user || atomUser
		if (
			currentUser &&
			currentUser.role !== UserRoleEnum.TRANSLATOR &&
			!isRedirectChecked
		) {
			router.push(ROUTES.cabinet.qrCodes)
		} else {
			setIsRedirectChecked(true)
		}
	}, [])

	useEffect(() => {
		if (onboardingQrId) {
			setOnboardingQrId(onboardingQrId)
		}
	}, [onboardingQrId])

	const form = useForm({
		defaultValues: { email: defaultEmail ?? '', password: '' },
		mode: 'onTouched',
	})

	// Persist email on pagehide event only.
	useEffect(() => {
		if (!onboardingQrId) return
		const persistDraft = () =>
			setSignUpDefaultEmail((current) =>
				getNextSignUpDefaultEmail(current, {
					onboardingQrId,
					defaultEmail,
					previousEmail,
					watchedEmail: form.getValues('email'),
				}),
			)
		window.addEventListener('pagehide', persistDraft)
		return () => {
			window.removeEventListener('pagehide', persistDraft)
			persistDraft()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form, onboardingQrId, setSignUpDefaultEmail])

	const authorizeUser = async (
		user: User,
		accessToken: string,
		authMethod: string,
		isNewAccount: boolean,
		isLogin = false,
	): Promise<void> => {
		if (user.use2fa) {
			setPending2faAccessToken(accessToken)
			const loginSearchParams = new URLSearchParams({
				[SEARCH_PARAMS.auth.key]: SEARCH_PARAMS.auth.login2fa,
				...(isOnboardingFlow && { 'onboarding-flow': 'true' }),
				...(isLogin &&
					isPopupMode && {
						[SEARCH_PARAMS.auth.surface]:
							SEARCH_PARAMS.auth.surfaceModal,
					}),
			})
			router.push(`${ROUTES.auth.login}?${loginSearchParams.toString()}`)
			return
		}

		setUser(user)
		setLastAuthProvider(authMethod)
		identifyUser({ id: user.id, email: user.email })
		if (isLogin) {
			posthogClient.capture('user_signed_in', {
				auth_method: authMethod,
				auth_surface: isPopupMode ? 'modal' : 'page',
				builder_session_id: getBuilderSessionId(),
			})
		}
		if (isNewAccount) {
			pushToDataLayer({
				event: 'sign_up',
				signup_email: user.email,
			})
			setNewUserCookie('true', { days: 1 })
		}
		setAuthToken(accessToken, user.role)

		if (isOnboardingFlow) {
			try {
				const qrId =
					onboardingQrId ?? getOnboardingQrId()
				if (!qrId) {
					throw new Error('Missing guest QR ID.')
				}
				await qrCodesApi.publishQr(qrId)
				router.push(
					getOnboardingLandingRoute({ user, qrId, isNewAccount }),
				)
			} catch (error) {
				console.error('Publishing new user QR failed:', error)
				showNotification(getValidErrorMessage(error, t), {
					type: 'error',
					toastId: 'signup',
				})
				router.push(getOnboardingLandingRoute({ user, isNewAccount }))
			}
			router.refresh()
		} else {
			setIsPostSignUpVisit(true)
			router.push(ROUTES.generator.typeStep)
		}
	}

	const onSocialSubmit = async (payload: {
		token: string
		type: 'google' | 'facebook' | 'apple'
		signup?: boolean
	}): Promise<void> => {
		setIsLoading(true)
		const { token, type, signup } = payload
		if (!token) {
			return
		}
		posthogClient.capture('click_auth_social', {
			auth_surface: isPopupMode ? 'modal' : 'page',
			auth_method: type,
			builder_session_id: getBuilderSessionId(),
		})
		try {
			switch (type) {
				case 'google': {
					const {
						data: { accessToken, user, isNewUser },
					} = await getApiClient().post(
						API_ROUTES.auth.googleSignUp,
						{
							token,
							language: locale,
							timezone,
						},
					)
					await authorizeUser(
						user,
						accessToken,
						type,
						isNewUser === true,
					)
					break
				}
				case 'facebook': {
					const {
						data: { accessToken, user, isNewUser },
					} = await getApiClient().post(
						API_ROUTES.auth.facebookSignUp,
						{
							token,
							language: locale,
							timezone,
						},
					)
					await authorizeUser(
						user,
						accessToken,
						type,
						isNewUser === true,
					)
					break
				}
				case 'apple': {
					const {
						data: { accessToken, user, isNewUser },
					} = await getApiClient().post(
						signup
							? API_ROUTES.auth.appleSignUp
							: API_ROUTES.auth.appleLogin,
						signup
							? { code: token, language: locale, timezone }
							: { code: token },
					)
					await authorizeUser(
						user,
						accessToken,
						type,
						Boolean(signup) && isNewUser === true,
					)
					break
				}
			}
		} catch (error) {
			console.error(error)
			const errorMessage = getValidErrorMessage(error, t)
			showNotification(errorMessage, { type: 'error', containerId })
			posthogClient.capture('show_auth_error', {
				auth_surface: isPopupMode ? 'modal' : 'page',
				auth_method: type,
				error_reason: 'auth_failed',
				builder_session_id: getBuilderSessionId(),
			})
			setIsLoading(false)
		}
	}

	const postLogin = async (body: {
		email: string
		password?: string
		otp?: string
	}) => {
		const builderSessionId = getBuilderSessionId()
		const { data } = await getApiClient().post<
			TAuthSuccess | TLoginChallenge
		>(API_ROUTES.auth.login, {
			...body,
			...(builderSessionId && { builderSessionId }),
		})
		return data
	}

	const handleLoginResponse = async (
		email: string,
		data: TAuthSuccess | TLoginChallenge,
		authMethod: string,
	): Promise<void> => {
		if (!isLoginChallenge(data)) {
			await authorizeUser(
				data.user,
				data.accessToken,
				authMethod,
				false,
				true,
			)
			return
		}
		if (data.status === 'OTP_SENT') {
			setOtpStep({
				email,
				resendCooldownSeconds:
					data.resendCooldownSeconds ??
					DEFAULT_OTP_RESEND_COOLDOWN_SECONDS,
			})
		} else {
			setPasswordEmail(email)
		}
		setIsLoading(false)
	}

	const handleVerifyOtp = async (code: string): Promise<void> => {
		if (!otpStep) return
		const { email } = otpStep
		try {
			await handleLoginResponse(
				email,
				await postLogin({ email, otp: code }),
				'email',
			)
		} catch (error) {
			posthogClient.capture('show_auth_error', {
				auth_surface: isPopupMode ? 'modal' : 'page',
				auth_method: 'email',
				error_reason: 'auth_failed',
				builder_session_id: getBuilderSessionId(),
			})
			throw error
		}
	}

	const handleResendOtp = async (): Promise<number> => {
		if (!otpStep) return 0
		const { email } = otpStep
		const data = await postLogin({ email })
		if (isLoginChallenge(data) && data.status === 'OTP_SENT') {
			return (
				data.resendCooldownSeconds ??
				DEFAULT_OTP_RESEND_COOLDOWN_SECONDS
			)
		}
		await handleLoginResponse(email, data, 'email')
		return 0
	}

	const onSubmit = async (values: { email: string; password: string }) => {
		const builderSessionId = getBuilderSessionId()
		const isPasswordLogin = passwordEmail !== null
		try {
			setIsLoading(true)
			if (isPasswordLogin) {
				posthogClient.capture('submit_auth_password', {
					auth_surface: isPopupMode ? 'modal' : 'page',
					auth_method: 'password',
					auth_mode: 'signin',
					email_valid: true,
					builder_session_id: builderSessionId,
				})
				await handleLoginResponse(
					passwordEmail,
					await postLogin({
						email: passwordEmail,
						password: values.password,
					}),
					'password',
				)
				return
			}
			if (hidePassword) {
				posthogClient.capture('submit_auth_email', {
					auth_surface: isPopupMode ? 'modal' : 'page',
					auth_method: 'email',
					email_valid: true,
					builder_session_id: getBuilderSessionId(),
					form_trigger: 'qr_download',
				})
				const effectiveOnboardingQrId =
					onboardingQrId ?? getOnboardingQrId()
				const payloadPreviousEmail = getPayloadPreviousEmail({
					storedDefaultEmail,
					onboardingQrId: effectiveOnboardingQrId,
					previousEmail,
					defaultEmail,
				})
				let signUpData: TAuthSuccess
				try {
					const { data } = await getApiClient().post<TAuthSuccess>(
						API_ROUTES.auth.onboardingSignUp,
						{
							email: values.email,
							...(payloadPreviousEmail && {
								previousEmail: payloadPreviousEmail,
								onboardingQrId: effectiveOnboardingQrId,
							}),
							language: locale,
							timezone,
							...(builderSessionId && { builderSessionId }),
						},
					)
					signUpData = data
				} catch (error) {
					if (
						!isOnboardingFlow ||
						!isExistingAccountConflict(error)
					) {
						throw error
					}
					await handleLoginResponse(
						values.email,
						await postLogin({ email: values.email }),
						'email',
					)
					return
				}
				const { accessToken, user } = signUpData
				if (effectiveOnboardingQrId) {
					setSignUpDefaultEmail({
						qrId: effectiveOnboardingQrId,
						previousEmail: values.email,
						draftEmail: values.email,
					})
				}
				await authorizeUser(
					user,
					accessToken,
					'email',
					isNewlyCreatedAccount({
						createdAt: user.createdAt,
					}),
				)
			} else {
				const {
					data: { accessToken, user },
				} = await getApiClient().post(API_ROUTES.auth.signUp, {
					...values,
					language: locale,
					timezone,
					...(builderSessionId && { builderSessionId }),
				})
				await authorizeUser(user, accessToken, 'password', true)
			}
		} catch (error) {
			console.error(error)
			const errorMessage = getValidErrorMessage(error, t)
			showNotification(errorMessage, {
				type: 'error',
				toastId: 'signup',
				containerId,
			})
			posthogClient.capture('show_auth_error', {
				auth_surface: isPopupMode ? 'modal' : 'page',
				auth_method:
					hidePassword && !isPasswordLogin ? 'email' : 'password',
				error_reason: 'auth_failed',
				builder_session_id: getBuilderSessionId(),
			})
			setIsLoading(false)
			return error
		}
	}

	const responseGoogle = (accessToken: string) => {
		if (!accessToken) {
			return
		}
		onSocialSubmit({ token: accessToken, type: 'google' })
	}

	const responseFacebook = ({ accessToken }: { accessToken: string }) => {
		if (!accessToken) {
			return
		}
		onSocialSubmit({ token: accessToken, type: 'facebook' })
	}

	const handleFacebookEmailDeclined = () => {
		showNotification(
			t('public__api__errors__facebookEmailPermissionError'),
			{
				type: 'error',
				containerId,
			},
		)
		posthogClient.capture('show_auth_error', {
			auth_surface: isPopupMode ? 'modal' : 'page',
			auth_method: 'facebook',
			error_reason: 'facebook_email_declined',
			builder_session_id: getBuilderSessionId(),
		})
	}

	const handleAppleSubmit = (token: string, signup?: boolean) =>
		onSocialSubmit({ token, type: 'apple', signup })

	const handleAppleError = () => {
		setIsLoading(false)
		showNotification(t('public__generic__unexpected_error'), {
			type: 'error',
			containerId,
		})
		posthogClient.capture('show_auth_error', {
			auth_surface: isPopupMode ? 'modal' : 'page',
			auth_method: 'apple',
			error_reason: 'auth_failed',
			builder_session_id: getBuilderSessionId(),
		})
	}

	const handleSubmit = (
		_:
			| React.MouseEvent<HTMLButtonElement, MouseEvent>
			| React.KeyboardEvent<Element>,
	) => {
		form.handleSubmit(onSubmit)()
	}

	const getSignUpPage = () => {
		if (isOnboardingFlow) {
			return (
				<SignUpPageWelcome
					responseGoogle={responseGoogle}
					responseFacebook={responseFacebook}
					handleFacebookEmailDeclined={handleFacebookEmailDeclined}
					handleAppleSubmit={handleAppleSubmit}
					handleAppleError={handleAppleError}
					onSubmit={handleSubmit}
					isLoading={isLoading}
					hideSocials={hideSocials}
					hidePassword={hidePassword}
					isPopupMode={isPopupMode}
					showPasswordField={!!passwordEmail}
					onPasswordStepBack={() => {
						setPasswordEmail(null)
						form.resetField('password')
					}}
					existingAccountStep={
						otpStep && (
							<SignUpExistingAccountStep
								email={otpStep.email}
								resendCooldownSeconds={
									otpStep.resendCooldownSeconds
								}
								containerId={containerId}
								onBack={() => setOtpStep(null)}
								onVerifyOtp={handleVerifyOtp}
								onResendOtp={handleResendOtp}
							/>
						)
					}
				/>
			)
		} else {
			return (
				<SignUpPageDirect
					responseGoogle={responseGoogle}
					responseFacebook={responseFacebook}
					handleFacebookEmailDeclined={handleFacebookEmailDeclined}
					handleAppleSubmit={handleAppleSubmit}
					handleAppleError={handleAppleError}
					onSubmit={handleSubmit}
					isLoading={isLoading}
					hideSocials={hideSocials}
					hidePassword={hidePassword}
				/>
			)
		}
	}

	if (!isRedirectChecked) {
		// To prevent flickering
		return null
	}

	return <FormProvider {...form}>{getSignUpPage()}</FormProvider>
}
